
import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts a string to a slug format (lowercase, hyphenated)
 */
export function slugify(text: string): string {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')        // Replace spaces with -
    .replace(/&/g, '-and-')      // Replace & with 'and'
    .replace(/[^\w\-]+/g, '')    // Remove all non-word characters
    .replace(/\-\-+/g, '-');     // Replace multiple - with single -
}

/**
 * Format numbers for display (e.g., 1000 -> 1K, 1000000 -> 1M)
 */
export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
}

/**
 * Interface defining the structure of an itinerary item
 */
export interface ItineraryItem {
  title: string;
  description: string;
}

/**
 * Validates and normalizes an itinerary item
 */
export const normalizeItineraryItem = (item: Partial<ItineraryItem>): ItineraryItem => {
  return {
    title: item.title || '',
    description: item.description || ''
  };
};

/**
 * Safely converts JSON data to ItineraryItem array
 */
export const safelyParseItineraryItems = (jsonData: any): ItineraryItem[] => {
  if (!jsonData || !Array.isArray(jsonData)) {
    return [];
  }
  
  return jsonData.map((item: any) => ({
    title: typeof item.title === 'string' ? item.title : '',
    description: typeof item.description === 'string' ? item.description : ''
  }));
};

/**
 * Converts itinerary highlights between form format and database format
 */
export const convertItineraryFormat = {
  // Convert from database format to form format
  toFormFormat: (items: any): ItineraryItem[] => {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return [{ title: '', description: '' }];
    }
    return safelyParseItineraryItems(items);
  },
  
  // Convert from form format to database format (for Supabase JSON)
  toDatabaseFormat: (items: Partial<ItineraryItem>[]): any => {
    return items.map(normalizeItineraryItem);
  }
};
